import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { StarIcon } from "lucide-react";

import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Icon } from "simplydo/react/ui/icon";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { TooltipButton } from "components/lib/UI";

import type { IdeaPreviewIdea } from "../types";

type FollowActionProps = { idea: IdeaPreviewIdea };

const FollowAction = ({ idea }: FollowActionProps) => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const followObject = util.userIsFollowingContextObject(
    user!,
    "idea",
    idea?._id,
  );

  const updateUser = useCallback(
    (nextUser: UserMe) => dispatch(actions.user.receiveUser(nextUser)),
    [dispatch],
  );

  const followIdea = useCallback(() => {
    api.ideas.follow(
      idea._id,
      () => {
        toast.success(
          `${t("common:capitalise", { key: "generic.idea" })} followed`,
        );
        followObject.following = true;
        followObject.unfollowed = false;

        const oldFollowing = (user?.following || []).filter(
          (f) => f._id !== idea?._id,
        );
        const newFollowing = [followObject, ...oldFollowing];

        updateUser({ ...user!, following: newFollowing });
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [idea, updateUser, user, t, followObject]);

  const unfollowIdea = useCallback(() => {
    api.ideas.unfollow(
      idea._id,
      () => {
        toast(`${t("common:capitalise", { key: "generic.idea" })} unfollowed`);
        followObject.following = false;
        followObject.unfollowed = true;

        const oldFollowing = (user?.following || []).filter(
          (f) => f._id !== idea?._id,
        );
        const newFollowing = [followObject, ...oldFollowing];

        updateUser({ ...user!, following: newFollowing });
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [idea, updateUser, user, t, followObject]);

  return (
    <TooltipButton
      onClick={() => {
        if (followObject?.following) {
          unfollowIdea();
        } else {
          followIdea();
        }
      }}
      tooltip={`Follow ${t("generic.ideaWithArticle")} to receive notifications when somebody likes it or posts a comment. When creating or interacting with ${t("generic.ideaWithArticle")} you will automatically start following it.`}
      color={followObject?.following ? "secondary" : undefined}
    >
      <Icon icon={followObject?.following ? StarIcon : StarIcon} />
    </TooltipButton>
  );
};

export default FollowAction;
